import React from 'react';
import { motion } from 'framer-motion';
import {MessageCircleDashed as MessageCircle, Sparkles, Brain, Smartphone, Clock, Mic} from 'lucide-react';

const features = [
  { icon: MessageCircle, text: 'Conversação real pelo WhatsApp' },
  { icon: Clock, text: 'Disponível 24h por dia, 7 dias por semana' },
  { icon: Mic, text: 'Prática de pronúncia e fala desde o primeiro dia' },
  { icon: Brain, text: 'Aprende seu ritmo e adapta as lições' },
  { icon: Smartphone, text: 'Direto do seu celular, onde você estiver' },
  { icon: Sparkles, text: '+500 horas de conteúdo autoral' },
];

const ecosystem = [
  'Lives semanais com professores reais',
  'Comunidade ativa',
  'App próprio iOS e Android',
  'Método GO4D alinhado ao CEFR',
  'Certificação TOEFL iBT',
];

export function LucyAISection() {
  return (
    <section className="relative bg-gradient-to-br from-gray-900 via-blue-950 to-indigo-950 py-16 md:py-24 overflow-hidden">
      {/* Efeitos de fundo */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
      </div>
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute top-0 left-0 w-full h-full opacity-30"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41Ii8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+\")",
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Título principal */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-white">LUCY </span>
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">IA</span>
          </h2>
          <p className="text-blue-100 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
            A primeira tutora de inglês <strong className="text-white">AI-First</strong> do Brasil integrada a um ecossistema completo de fluência.
          </p>
        </motion.div>

        {/* Proposta de valor */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex-1">
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
              Aprenda praticando conversação de verdade
            </h3>

            <p className="text-blue-100 text-lg md:text-xl leading-relaxed mb-4">
              Com a Lucy, você aprende praticando <strong className="text-white">conversação de verdade</strong> com sua professora particular, direto do seu celular pelo <strong className="text-green-400">WhatsApp</strong>.
            </p>

            <p className="text-blue-200 text-base md:text-lg leading-relaxed mb-8 opacity-80">
              E não estudando gramática isolada ou repetindo frases feitas como em cursos e métodos de escolas de inglês tradicionais.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.07 }}
                  className="flex items-center gap-3 bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-3 border border-white border-opacity-10">
                  <f.icon className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  <span className="text-blue-100 text-sm font-medium">{f.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Imagem do ecossistema */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-shrink-0 w-full max-w-sm lg:max-w-md">
            <img
              src="https://static.lumi.new/85/8578737d31747385321aad267a0ff70a.webp"
              alt="Ecossistema EW Pass com Lucy IA"
              className="w-full rounded-2xl shadow-2xl" />
          </motion.div>
        </div>

        {/* Ecossistema */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white bg-opacity-5 backdrop-blur-sm border border-white border-opacity-10 rounded-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
              <span className="text-white">Lucy é o cérebro.</span>{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">EW Pass é o corpo.</span>
            </h3>
            <p className="text-blue-200 text-lg md:text-xl opacity-90">
              O ecossistema completo que potencializa a Lucy:
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {ecosystem.map((item, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                className="inline-flex items-center gap-2 bg-purple-500 bg-opacity-20 border border-purple-400 border-opacity-30 rounded-full px-4 py-2 text-sm text-blue-100">
                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                {item}
              </motion.span>
            ))}
          </div>

          <h4 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center italic">
            Tudo alimenta a inteligência da Lucy.
          </h4>
        </motion.div>
      </div>
    </section>);

}
